import { useAppSelector } from "@/app/hooks"
import { useRef, useEffect } from "react"

export function AnimatedBackground() {
  const homeSlice = useAppSelector(state => state.home)
  const canvasRef = useRef<HTMLCanvasElement>(null)

  const layersRef = useRef([
    { speed: 10, offset: window.innerWidth / (Math.random() * 4), color: "rgb(240, 240, 240)" },
    { speed: 35, offset: window.innerWidth / (Math.random() * 3), color: "rgb(220, 220, 220)" },
    { speed: 70, offset: 0, color: "rgb(200, 200, 200)" },
  ])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext("2d")
    if (!ctx) return

    const resize = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight

      // Reset the flipped coordinate system
      ctx.setTransform(1, 0, 0, 1, 0, 0) // clear any previous transforms
      ctx.translate(0, canvas.height)
      ctx.scale(1, -1)
    }
    resize()
    window.addEventListener("resize", resize)

    let lastTime = performance.now()
    let animationFrameId: number
    const notRunningAndNativeWordActive = homeSlice.gameState !== "running" || homeSlice.nativeWordActive

    const draw = (time: number) => {
      let delta = (time - lastTime) / 1000
      lastTime = time

      if (notRunningAndNativeWordActive) delta = 0 // pause animation

      ctx.clearRect(0, 0, canvas.width, canvas.height)

      layersRef.current.forEach((layer, index) => {
        // update offset (modulo the tile width) so it never grows unbounded
        const tile = canvas.width // distance between ellipse centers
        layer.offset = (layer.offset + layer.speed * delta) % tile

        // geometry
        const y = -100 - index * 50 // flipped coordinates
        const radiusX = canvas.width / 2 + index * 50
        const radiusY = Math.max(10, canvas.width / 4 - index * 100) // avoid negative

        ctx.fillStyle = layer.color

        // Normalize offset to [0, tile)
        const base = ((layer.offset % tile) + tile) % tile

        // Start drawing from the first center that could have any visible pixels on screen:
        // leftmost visible pixel is at x = -radiusX
        // The first center <= (-radiusX) is: x0 = -radiusX - base
        let x = -radiusX - base

        // Draw across the whole visible span, including one extra tile to cover the right spill
        const rightLimit = canvas.width + radiusX
        for (; x < rightLimit; x += tile) {
          ctx.beginPath()
          ctx.ellipse(x, y, radiusX, radiusY, 0, 0, Math.PI, false)
          ctx.fill()
        }
      })

      animationFrameId = requestAnimationFrame(draw)
    }

    draw(performance.now())

    return () => {
      cancelAnimationFrame(animationFrameId)
      window.removeEventListener("resize", resize)
    }
  }, [homeSlice.gameState, homeSlice.nativeWordActive])

  return <canvas ref={canvasRef} className="fixed top-0 left-0 w-full h-full -z-10" />
}
